import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from './DataTable';
import { theme } from '../theme';

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
};

const overlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem',
};

function parseCustom(custom) {
  if (!custom) return {};
  if (typeof custom === 'object') return custom;
  try { return JSON.parse(custom) || {}; } catch { return {}; }
}

// Detecta vista de celular para ajustar paddings y evitar desbordes
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function ContactListView({ token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const authH = { Authorization: `Bearer ${token}` };
  const isMobile = useIsMobile();

  const [list, setList] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Campos personalizados de la empresa → columnas dinámicas del DataTable
  const [customFields, setCustomFields] = useState([]);
  const loadCustomFields = useCallback(() => {
    fetch('/api/templates/custom-fields', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setCustomFields(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetch(`/api/contact-lists/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setList)
      .catch(() => setNotFound(true));
    loadCustomFields();
  }, [id, token, loadCustomFields]);

  // --- Alta individual ---
  const [newPhone, setNewPhone] = useState('');
  const [newCustom, setNewCustom] = useState([]); // [{ key, value }]
  const [addError, setAddError] = useState('');

  // --- Importación ---
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  // --- Búsqueda ---
  const [searchField, setSearchField] = useState('id'); // 'id' = número; o nombre de campo personalizado
  const [searchInput, setSearchInput] = useState('');
  const [searchParams, setSearchParams] = useState({}); // filtros activos enviados al DataTable

  const runSearch = () => {
    const q = searchInput.trim();
    setSearchParams(q ? { field: searchField, q } : {});
  };
  const clearSearch = () => {
    setSearchInput('');
    setSearchField('id');
    setSearchParams({});
  };

  // --- Edición de contacto ---
  const [editContact, setEditContact] = useState(null); // { id, custom }
  const [editRows, setEditRows] = useState([]); // [{ key, value }]
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // Columnas: número + cada campo personalizado conocido
  const columns = [
    { key: 'id', label: 'Número' },
    ...customFields.map((f) => ({ key: f.name, label: f.name })),
  ];

  // ---- Alta individual ----
  const addCustomRow = () => setNewCustom((rows) => [...rows, { key: '', value: '' }]);
  const updateCustomRow = (i, field, val) => setNewCustom((rows) => rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const removeCustomRow = (i) => setNewCustom((rows) => rows.filter((_, idx) => idx !== i));

  const handleAddContact = async () => {
    const phone = newPhone.trim();
    if (!phone) { setAddError('Ingresa un número'); return; }
    const custom = {};
    for (const { key, value } of newCustom) {
      const k = key.trim();
      if (k && value.trim() !== '') custom[k] = value.trim();
    }
    setAddError('');
    try {
      const res = await fetch(`/api/contact-lists/${id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify({ phone, custom }),
      });
      const data = await res.json();
      if (!res.ok) { setAddError(data.message || 'Error al agregar'); return; }
      setNewPhone('');
      setNewCustom([]);
      loadCustomFields();
      tableRef.current?.reload();
      showSuccess('Contacto agregado.');
    } catch {
      setAddError('Error de conexión');
    }
  };

  // ---- Importación CSV ----
  const downloadTemplate = () => {
    fetch('/api/contact-lists/template', { headers: authH })
      .then((r) => r.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla_contactos.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const handleImport = async () => {
    if (!importFile) { setImportMsg('Selecciona un archivo'); return; }
    setImporting(true);
    setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await fetch(`/api/contact-lists/${id}/import`, { method: 'POST', headers: authH, body: fd });
      const data = await res.json();
      setImportMsg(data.message || (res.ok ? 'Importado' : 'Error al importar'));
      if (res.ok) {
        setImportFile(null);
        loadCustomFields();
        tableRef.current?.reload();
      }
    } catch {
      setImportMsg('Error de conexión');
    } finally {
      setImporting(false);
    }
  };

  // ---- Edición ----
  const openEdit = (row) => {
    const custom = parseCustom(row.custom);
    setEditContact(row);
    setEditRows(Object.entries(custom).map(([key, value]) => ({ key, value: String(value) })));
    setEditError('');
  };

  const updateEditRow = (i, field, val) => setEditRows((rows) => rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const removeEditRow = (i) => setEditRows((rows) => rows.filter((_, idx) => idx !== i));
  const addEditRow = () => setEditRows((rows) => [...rows, { key: '', value: '' }]);

  const handleEditSave = async () => {
    const custom = {};
    for (const { key, value } of editRows) {
      const k = key.trim();
      if (k && String(value).trim() !== '') custom[k] = String(value).trim();
    }
    setEditError('');
    setEditLoading(true);
    try {
      const res = await fetch(`/api/contact-lists/${id}/contacts/${encodeURIComponent(editContact.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify({ custom }),
      });
      const data = await res.json();
      if (!res.ok) { setEditError(data.message || 'Error al guardar'); return; }
      setEditContact(null);
      loadCustomFields();
      tableRef.current?.reload();
      showSuccess('Contacto actualizado.');
    } catch {
      setEditError('Error de conexión');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRemove = async (row) => {
    if (!window.confirm(`¿Quitar ${row.id} de esta lista?`)) return;
    try {
      const res = await fetch(`/api/contact-lists/${id}/contacts/${encodeURIComponent(row.id)}`, { method: 'DELETE', headers: authH });
      if (!res.ok) return;
      tableRef.current?.reload();
      showSuccess('Contacto removido de la lista.');
    } catch { /* noop */ }
  };

  const actionBtn = (color) => ({
    padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${color}`,
    borderRadius: '6px', color, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  });

  if (notFound) {
    return (
      <main style={{ padding: '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto', color: theme.colors.textMuted }}>
        Lista no encontrada. <button onClick={() => navigate('/listas-contactos')} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer' }}>Volver</button>
      </main>
    );
  }

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
      <button onClick={() => navigate('/listas-contactos')} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1rem', padding: 0 }}>
        ← Volver a listas
      </button>

      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`, boxSizing: 'border-box' }}>

        <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>
          Contactos {list ? `— ${list.name}` : ''}
        </h1>
        {list?.description && <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1.2rem' }}>{list.description}</p>}

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', margin: '1rem 0', color: theme.colors.success, fontSize: '0.9rem' }}>
            {successMsg}
          </div>
        )}

        {/* Alta individual + importación */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {/* Alta individual */}
          <div style={{ flex: '1 1 300px', minWidth: 0, border: `1px solid ${theme.colors.border}`, borderRadius: '10px', padding: '1rem', boxSizing: 'border-box' }}>
            <label style={labelStyle}>Agregar contacto</label>
            <input
              type="text"
              placeholder="Número (ej: 573001234567)"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))}
              style={{ ...inputStyle, marginBottom: '0.6rem' }}
            />
            {newCustom.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input list="clv-fields" placeholder="Campo" value={row.key} onChange={(e) => updateCustomRow(i, 'key', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Valor" value={row.value} onChange={(e) => updateCustomRow(i, 'value', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeCustomRow(i)} style={{ ...actionBtn(theme.colors.error), padding: '0 12px' }}>×</button>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem' }}>
              <button onClick={addCustomRow} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>+ Campo personalizado</button>
              <button onClick={handleAddContact} style={{ padding: '8px 18px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer' }}>Agregar</button>
            </div>
            {addError && <p style={{ color: theme.colors.error, fontSize: '0.82rem', margin: '0.6rem 0 0' }}>{addError}</p>}
          </div>

          {/* Importación */}
          <div style={{ flex: '1 1 300px', minWidth: 0, border: `1px solid ${theme.colors.border}`, borderRadius: '10px', padding: '1rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ ...labelStyle, margin: 0 }}>Importar desde CSV</label>
              <button onClick={downloadTemplate} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}>Descargar plantilla</button>
            </div>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0 0 0.6rem' }}>
              Una columna de número (numero/celular/telefono/whatsapp). Cualquier otra columna se guarda como campo personalizado.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setImportFile(e.target.files[0]); setImportMsg(''); }} style={{ color: theme.colors.text, fontSize: '0.82rem', flex: '1 1 140px', minWidth: 0, maxWidth: '100%' }} />
              <button onClick={handleImport} disabled={importing || !importFile} style={{ padding: '8px 18px', backgroundColor: importing || !importFile ? theme.colors.border : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.88rem', fontWeight: '700', cursor: importing || !importFile ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
            {importMsg && <p style={{ color: /creado|actualizado|importado/i.test(importMsg) ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', margin: '0.6rem 0 0' }}>{importMsg}</p>}
          </div>
        </div>

        <datalist id="clv-fields">
          {customFields.map((f) => <option key={f.id} value={f.name} />)}
        </datalist>

        {/* Buscador: por número o por un campo personalizado */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            style={{ ...inputStyle, flex: '1 1 160px', minWidth: 0, width: 'auto' }}
          >
            <option value="id">Número</option>
            {customFields.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
          </select>
          <input
            type="text"
            placeholder={searchField === 'id' ? 'Buscar por número...' : `Buscar en "${searchField}"...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            style={{ ...inputStyle, flex: '2 1 200px', minWidth: 0 }}
          />
          <button onClick={runSearch} style={{ padding: '10px 18px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Buscar
          </button>
          {(searchParams.q) && (
            <button onClick={clearSearch} style={{ padding: '10px 16px', backgroundColor: 'transparent', color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}`, borderRadius: '7px', fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Limpiar
            </button>
          )}
        </div>

        <DataTable
          ref={tableRef}
          columns={columns}
          fetchUrl={`/api/contact-lists/${id}/contacts`}
          token={token}
          params={searchParams}
          renderActions={(row) => (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={() => openEdit(row)} style={actionBtn(theme.colors.primary)}>Editar</button>
              <button onClick={() => handleRemove(row)} style={actionBtn(theme.colors.error)}>Quitar</button>
            </div>
          )}
        />
      </div>

      {/* Modal editar contacto */}
      {editContact && (
        <div onClick={() => !editLoading && setEditContact(null)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>Editar contacto</h2>
              <button onClick={() => !editLoading && setEditContact(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Número</label>
              <input type="text" value={editContact.id} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
            </div>

            <label style={labelStyle}>Información del contacto</label>
            <div style={{ overflowY: 'auto', flex: 1, marginBottom: '0.8rem' }}>
              {editRows.length === 0 && <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>Sin datos. Agrega un campo abajo.</p>}
              {editRows.map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input list="clv-fields" placeholder="Campo" value={row.key} onChange={(e) => updateEditRow(i, 'key', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <input placeholder="Valor" value={row.value} onChange={(e) => updateEditRow(i, 'value', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <button onClick={() => removeEditRow(i)} style={{ ...actionBtn(theme.colors.error), padding: '0 12px' }}>×</button>
                </div>
              ))}
            </div>
            <button onClick={addEditRow} style={{ background: 'none', border: 'none', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.85rem', padding: 0, marginBottom: '1rem', textAlign: 'left' }}>+ Agregar campo</button>

            {editError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{editError}</p>}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setEditContact(null)} disabled={editLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleEditSave} disabled={editLoading} style={{ flex: 1, padding: '10px', backgroundColor: editLoading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: editLoading ? 'not-allowed' : 'pointer' }}>{editLoading ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default ContactListView;
